// Default imports
import React from "react";
import { Row, Col, Container } from "react-bootstrap";
import WpImage from "./WpImage";

// Import GraphQl data
import { footerTopData } from "../data/options-footer-top.data";
import { wpUrlData } from "../data/wp-url.data";
import { normalizeWpLinkUrl } from "../helpers/globalHelpers";

// Import SVG
import Logo from "../images/white-logo-cropped.svg";
import IconEnvelope from "../icons/icon-envelope.svg";
import IconPhone from "../icons/icon-phone.svg";

const Footer = () => {
    const themeSettings = footerTopData() || {};
    const socials = themeSettings.socialMedia || [];
    const footerData = themeSettings.footer || {};

    // Footer URLs are typed by hand in WordPress, so they need normalising
    // before use: internal ones must carry the trailing slash the pages are
    // built with, and any left absolute against the WordPress host would
    // otherwise send visitors to the backend.
    const wpUrl = wpUrlData();
    const linkUrl = (url) => normalizeWpLinkUrl(url, wpUrl);

    const { tagline, certifications, copyright, columns = [] } = footerData;
    const footerBlocks = columns.flatMap((col) => col.blocks || []);

    const contactBlock = footerBlocks.find((b) => b.type === "contact");
    const navBlocks = footerBlocks.filter((b) => b.type !== "contact");

    const mobileNavOrder = [
        "solutions",
        "segments",
        "connectivity",
        "news and insights",
        "company",
        "resources",
        "legal",
    ];
    const getMobileBlockTitle = (block) =>
        (block.headerLink?.title || block.title || "").trim().toLowerCase();
    const getMobileBlockOrder = (block) => {
        const title = getMobileBlockTitle(block);
        const index = mobileNavOrder.indexOf(title);
        return index === -1 ? mobileNavOrder.length : index;
    };
    const mobileNavBlocks = [...navBlocks]
        .map((block, index) => ({ block, index }))
        .sort((a, b) => {
            const orderDiff =
                getMobileBlockOrder(a.block) - getMobileBlockOrder(b.block);
            return orderDiff || a.index - b.index;
        })
        .map(({ block }) => block);

    const currentYear = new Date().getFullYear();
    const displayCopyright = copyright
        ? copyright.replace("{{year}}", currentYear)
        : `© ${currentYear} Wyden. All Right Reserved.`;

    const handleBlankLinkClick = (event, link) => {
        if (link?.target !== "_blank" || !link?.url) return;

        event.preventDefault();
        event.nativeEvent?.stopImmediatePropagation?.();
        window.open(linkUrl(link.url), "_blank");
    };

    const renderContactInfo = (contactInfo) => (
        <div className="footer-menu footer-menu--contact">
            {contactInfo.address && (
                <p
                    className="footer-menu__text footer-menu__text--address"
                    dangerouslySetInnerHTML={{
                        __html: contactInfo.address.replace(/(\n|\\n)+/g, ""),
                    }}
                />
            )}
            {contactInfo.phone && (
                <p className="footer-menu__text footer-menu__text--icon-row">
                    <IconPhone className="footer-menu__contact-icon" />
                    <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
                </p>
            )}
            {contactInfo.email && (
                <p className="footer-menu__text footer-menu__text--icon-row">
                    <IconEnvelope className="footer-menu__contact-icon" />
                    <a href={`mailto:${contactInfo.email}`}>
                        {contactInfo.email}
                    </a>
                </p>
            )}
        </div>
    );

    const renderFooterBlock = (block, key) => (
        <div key={key} className="footer-columns__item-wrapper">
            <div className="footer-columns__item">
                <h6 className="footer-columns__header">
                    {block.headerLink ? (
                        <a
                            href={linkUrl(block.headerLink.url)}
                            target={block.headerLink.target}
                            className="footer-columns__header-link"
                            onClickCapture={(event) =>
                                handleBlankLinkClick(event, block.headerLink)
                            }
                        >
                            {block.headerLink.title || block.title}
                        </a>
                    ) : (
                        block.title
                    )}
                </h6>

                {block.type === "links" &&
                    block.links &&
                    block.links.length > 0 && (
                        <ul className="footer-menu">
                            {block.links.map((linkItem, linkIndex) => (
                                <li
                                    key={linkIndex}
                                    className="footer-menu__item"
                                >
                                    <a
                                        href={linkUrl(linkItem.link?.url)}
                                        target={linkItem.link?.target}
                                        className="footer-menu__link"
                                        onClickCapture={(event) =>
                                            handleBlankLinkClick(
                                                event,
                                                linkItem.link
                                            )
                                        }
                                    >
                                        {linkItem.link?.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
            </div>
        </div>
    );

    return (
        <footer className="footer">
            <Container>
                <Row className="gy-5">
                    <Col className="col-24 col-lg-8 col-xl-6">
                        <div className="footer__brand">
                            <Logo className="footer__logo" />

                            {tagline && (
                                <p
                                    className="footer__tagline"
                                    dangerouslySetInnerHTML={{
                                        __html: tagline.replace(
                                            /(\n|\\n)+/g,
                                            "<br/>"
                                        ),
                                    }}
                                />
                            )}

                            {certifications && (
                                <p
                                    className="footer__cert-text"
                                    dangerouslySetInnerHTML={{
                                        __html: certifications.replace(
                                            /(\n|\\n)+/g,
                                            "<br/>"
                                        ),
                                    }}
                                />
                            )}

                            <div className="footer__socials">
                                {socials.map(({ link, icon }) => (
                                    <a
                                        key={link.url}
                                        href={linkUrl(link.url)}
                                        title={link?.title}
                                        target={link.target}
                                        className="footer__social-item"
                                        onClickCapture={(event) =>
                                            handleBlankLinkClick(event, link)
                                        }
                                    >
                                        <WpImage
                                            className="footer__social-icon"
                                            image={icon}
                                        />
                                    </a>
                                ))}
                            </div>

                            {contactBlock?.contactInfo && (
                                <div className="footer__contact footer__contact--desktop">
                                    {contactBlock.title && (
                                        <h6 className="footer-columns__header footer__contact-header">
                                            {contactBlock.title}
                                        </h6>
                                    )}
                                    {renderContactInfo(
                                        contactBlock.contactInfo
                                    )}
                                </div>
                            )}

                            <p className="footer__copyright footer__copyright--desktop">
                                {displayCopyright}
                            </p>
                        </div>
                    </Col>

                    <Col className="col-24 col-lg-15 offset-lg-1 col-xl-16 offset-xl-2 footer__menus-desktop">
                        <Row className="gy-4">
                            {columns.map((col, colIndex) => {
                                const colNavBlocks = (col.blocks || []).filter(
                                    (b) => b.type !== "contact"
                                );
                                if (colNavBlocks.length === 0) return null;
                                return (
                                    <Col
                                        key={colIndex}
                                        className="col-24 col-md-8"
                                    >
                                        <div className="footer__menus-col">
                                            {colNavBlocks.map(
                                                (block, blockIndex) =>
                                                    renderFooterBlock(
                                                        block,
                                                        blockIndex
                                                    )
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Col>
                </Row>

                <Row className="footer__menus-mobile gy-4">
                    {mobileNavBlocks.map((block, blockIndex) => (
                        <Col key={blockIndex} className="col-24">
                            {renderFooterBlock(block, blockIndex)}
                        </Col>
                    ))}

                    {contactBlock?.contactInfo && (
                        <Col className="col-24 footer__contact--mobile">
                            <div className="footer-columns__item">
                                {contactBlock.title && (
                                    <h6 className="footer-columns__header">
                                        {contactBlock.title}
                                    </h6>
                                )}
                                {renderContactInfo(contactBlock.contactInfo)}
                            </div>
                        </Col>
                    )}
                </Row>

                <Row>
                    <Col>
                        <p className="footer__copyright footer__copyright--mobile">
                            {displayCopyright}
                        </p>
                    </Col>
                </Row>
            </Container>
        </footer>
    );
};

export default Footer;
